import clsx from 'clsx';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import ChevronDown from '../../assets/icons/chevron-down-icon.svg?react';
import OutlineEyeIcon from '../../assets/icons/outline-eye-icon.svg?react';
import FlagIcon from '../../assets/icons/flag-icon.svg?react';
import FilledRedFlagIcon from '../../assets/icons/filled-red-flag-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import Button from '../../components/Button';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import {
  GetReportsForOshaResponse,
  InvestigationTypeEnum,
} from '@EHSNavigator/backend';
import moment from 'moment';
import NoItemsFound from '../../components/NoItemsFound';

interface CreateOshaReportStepOneProps {
  otherClasses?: string;
  handleSteps: (steps: number) => void;
  excludedRow: string[];
  handleExcludeRow: React.Dispatch<
    React.SetStateAction<{ excludedRow: string[] }>
  >;
  data: GetReportsForOshaResponse;
  year: string;
}

const CreateOshaReportStepOne: React.FC<CreateOshaReportStepOneProps> = ({
  otherClasses,
  handleSteps,
  excludedRow,
  handleExcludeRow,
  data,
  year,
}) => {
  const createOshaReportStepOneClasses = clsx(otherClasses);
  const [state, setState] = useState({
    selectedRow: '',
  });
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const sm = useButtonSizeSmall();

  return (
    <div
      className={createOshaReportStepOneClasses}
      data-testid="create-osha-report-step-one"
    >
      <div className="flex lg:gap-10 xl:items-center xl:justify-between">
        <div className="flex flex-1 items-start gap-2.5">
          <button
            type="button"
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <div>
            <h1 className="h3 mb-1 lg:mb-0">OSHA 300</h1>
            <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              STEP 1 / 3
            </p>
          </div>
        </div>
        <div className="hidden items-center gap-15 lg:flex">
          <Button
            size="smaller"
            variant="secondary"
            href="/osha-reporting"
            otherClasses="w-174 text-brand-posh-pink  !border-brand-posh-pink hover:text-white  hover:!bg-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              dispatch(toggleModal('osha-reporting-proceed-confirmation'))
            }
            disable={excludedRow?.length === data?.reports?.length}
            type="button"
            size="smaller"
            otherClasses="w-174"
          >
            Proceed
          </Button>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-8 text-sm leading-4 font-medium lg:mt-15 lg:text-xl lg:leading-6">
        Kindly review the pre-populated OSHA 300 form for any discrepancies and
        changes you might want to address. Changes will not be possible after
        this step.
      </p>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-8 text-sm leading-4 font-medium lg:mt-15 lg:text-xl lg:leading-6">
        Ensure accuracy when submitting to OSHA - incorrect or fraudulent data
        may incur penalties.
      </p>
      <div className="my-8 flex items-start gap-9 lg:my-9">
        <div>
          <span className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-bold uppercase lg:text-xl lg:leading-6 lg:font-medium">
            Year
          </span>
          <h2 className="text-brand-byzantine font-inter-variable font-semibold">
            {year}
          </h2>
        </div>
        <div>
          <span className="font-inter-variable text-neutral-tones-text text-sm leading-4 font-bold uppercase lg:text-xl lg:leading-6 lg:font-medium">
            RECORDS
          </span>
          <h2 className="text-brand-byzantine font-inter-variable font-semibold">
            {`${data?.total} Reports`}
          </h2>
        </div>
      </div>
      <p className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-normal lg:text-xl lg:leading-6 lg:font-medium">
        Please note, <strong className="text-brand-posh-pink">flagged</strong>{' '}
        records will not be submitted.
      </p>

      {data?.reports?.length > 0 ? (
        <div className="mt-9 grid grid-cols-1 gap-2.5 sm:gap-5 lg:flex lg:flex-col lg:gap-2.5">
          {data?.reports?.map((node, index) => (
            <div
              key={index}
              className={clsx(
                'relative h-fit w-full border px-2.5 py-15 transition-all duration-300 lg:px-5',
                excludedRow.includes(node?.id)
                  ? 'border-brand-posh-pink bg-brand-posh-pink/10 rounded-10 border-2'
                  : state?.selectedRow === node?.id
                    ? 'rounded-10 bg-card-backgrounds/5 border-neutral-tones-text/10'
                    : 'hover:rounded-10 border-b-neutral-tones-text/10 hover:bg-card-backgrounds/5 hover:border-neutral-tones-text/10 border-transparent',
              )}
            >
              <div className="grid grid-cols-12 gap-x-5 gap-y-2.5">
                <div className="col-span-10 lg:col-span-4">
                  <span className="text-neutral-tones-text/50 font-inter-variable mb-2.5 block text-xs leading-4 font-bold lg:text-sm">
                    {node?.createdAt
                      ? moment(node.createdAt)
                          .format('MMMM D, YYYY')
                          .toUpperCase()
                      : ''}
                  </span>
                  <p className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-normal lg:text-xl lg:leading-6">
                    {node?.user?.fullName}
                  </p>
                </div>
                <div className="col-span-10 col-start-1 self-end lg:col-span-4 lg:col-start-auto">
                  <p className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-normal uppercase">
                    <strong className="font-bold normal-case">Ref#</strong>{' '}
                    {node?.investigationRefId}
                  </p>
                </div>
                <div className="col-span-2 col-start-11 row-span-3 row-start-1 flex items-center justify-end gap-25 lg:col-span-4 lg:col-start-auto lg:row-span-1 lg:row-start-auto">
                  <div className="bg-card-backgrounds/5 rounded-10 border-neutral-tones-text/10 hidden h-full items-center gap-15 border px-2 py-2.5 lg:flex">
                    <button
                      type="button"
                      onClick={() => {
                        handleExcludeRow((prevState) => {
                          const isExcluded = prevState.excludedRow.includes(
                            node?.id,
                          );
                          return {
                            ...prevState,
                            excludedRow: isExcluded
                              ? prevState.excludedRow.filter(
                                  (id: any) => id !== node?.id,
                                )
                              : [...prevState.excludedRow, node?.id],
                          };
                        });
                      }}
                      className="cursor-pointer transition-all duration-300 hover:scale-105"
                    >
                      {excludedRow.includes(node?.id) ? (
                        <FilledRedFlagIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                      ) : (
                        <FlagIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer transition-all duration-300 hover:scale-105"
                      onClick={() =>
                        window.open(`/injuries/${node?.id}`, '_blank')
                      }
                    >
                      <OutlineEyeIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                    </button>
                  </div>
                  <div className="border-l-neutral-tones-text/35 flex h-full items-center lg:border-l lg:pl-25">
                    <button
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          selectedRow:
                            state?.selectedRow === node?.id ? '' : node?.id,
                        }))
                      }
                      type="button"
                      className={clsx(
                        'cursor-pointer transition-all duration-300',
                        state?.selectedRow === node?.id ? 'rotate-180' : '',
                      )}
                    >
                      <ChevronDown className="[&>path]:fill-brand-byzantine h-8 w-8 lg:h-12 lg:w-12" />
                    </button>
                  </div>
                </div>
                <div className="bg-card-backgrounds/5 rounded-10 border-neutral-tones-text/10 col-span-10 flex h-full w-fit items-center gap-15 border px-2 py-2.5 lg:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      handleExcludeRow((prevState) => {
                        const isExcluded = prevState.excludedRow.includes(
                          node?.id,
                        );
                        return {
                          ...prevState,
                          excludedRow: isExcluded
                            ? prevState.excludedRow.filter(
                                (id: any) => id !== node?.id,
                              )
                            : [...prevState.excludedRow, node?.id],
                        };
                      });
                    }}
                    className="cursor-pointer"
                  >
                    {excludedRow.includes(node?.id) ? (
                      <FilledRedFlagIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                    ) : (
                      <FlagIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                    )}
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer"
                    onClick={() =>
                      window.open(`/injuries/${node?.id}`, '_blank')
                    }
                  >
                    <OutlineEyeIcon className="h-5 w-5 lg:h-8 lg:w-8" />
                  </button>
                </div>
              </div>
              {state?.selectedRow === node?.id && (
                <div className="mt-9 grid grid-cols-2 gap-x-5 gap-y-9 sm:grid-cols-12">
                  <div className="flex flex-col gap-1 sm:col-span-4">
                    <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                      Injury or Illness
                    </span>
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      {node?.type}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 sm:col-span-4">
                    <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                      Affected Body Parts
                    </span>
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      {node?.afffectedBodyPartsSentence}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 sm:col-span-4">
                    <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                      Type of Loss
                    </span>
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      {node?.typeOfLoss}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 sm:col-span-4">
                    <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                      Classification
                    </span>
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      {node?.classification}
                    </p>
                  </div>
                  {node?.type === InvestigationTypeEnum.Illness && (
                    <div className="flex flex-col gap-1 sm:col-span-4">
                      <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                        Classify Illness
                      </span>
                      <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                        {node?.classifyIllness}
                      </p>
                    </div>
                  )}
                  {node?.daysAwayFromWork && (
                    <div className="flex flex-col gap-1 sm:col-span-4">
                      <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                        Days Away From Work
                      </span>
                      <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                        {node?.daysAwayFromWork}
                      </p>
                    </div>
                  )}
                  {node?.jobRestrictionDays && (
                    <div className="flex flex-col gap-1 sm:col-span-4">
                      <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                        Restricted Work Activity or Job Transfer
                      </span>
                      <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                        {node?.jobRestrictionDays}
                      </p>
                    </div>
                  )}
                  {node?.dateOfDeath && (
                    <div className="flex flex-col gap-1 sm:col-span-4">
                      <span className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-bold">
                        Date Of Death
                      </span>
                      <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                        {node?.dateOfDeath}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <NoItemsFound message="No Reports found" />
      )}
      {data?.reports?.length > 0 && (
        <div className="mt-8 flex items-center justify-center gap-15 lg:hidden">
          <Button
            size="smaller"
            variant="secondary"
            href="/osha-reporting"
            otherClasses="w-2/4 sm:w-174 text-brand-posh-pink  !border-brand-posh-pink hover:text-white  hover:!bg-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() =>
              dispatch(toggleModal('osha-reporting-proceed-confirmation'))
            }
            size="smaller"
            otherClasses="w-2/4 sm:w-174"
            disable={excludedRow?.length === data?.reports?.length}
          >
            Proceed
          </Button>
        </div>
      )}

      {['osha-reporting-proceed-confirmation'].includes(modalName) && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
          }}
        >
          <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
            <WarningIcon className="[&>g>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
            <h4 className="text-brand-byzantine">Are you sure?</h4>
            <p className="font-inter-variable text-neutral-tones-text -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              {excludedRow?.length === 0 ? (
                `Clicking on continue would send the records to Osha.`
              ) : (
                <>
                  {' '}
                  You have{' '}
                  <strong className="text-brand-posh-pink font-bold">
                    flagged {excludedRow?.length}
                  </strong>{' '}
                  records. If you continue, they will not be submitted to OSHA.
                  Are you sure you want to continue?
                </>
              )}
            </p>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
            <Button
              variant="secondary"
              type="button"
              size={sm}
              onClick={() => dispatch(toggleModal(''))}
              otherClasses=" text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleSteps(2);
                dispatch(toggleModal(''));
              }}
              type="button"
              size={sm}
            >
              {excludedRow?.length === 0 ? `Continue` : `Continue Anyway`}
            </Button>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default CreateOshaReportStepOne;
